import { useEffect, useRef, useState } from 'react';
import fr from 'facet-rest';

import useCollection from './useCollection';

/**
 * useAffordance
 *
 * Please use "useLink" instead for a consistent interface to fetch data.
 *
 * Reason(s) for deprecation:
 * - mountedRef logic is used to mean stale rather than mounted.
 * - Does not support updating the collection and refetching new data with the updated collection
 *
 * @deprecated
 */
export default function useAffordance(linkOrLinkUrl, args = {}) {
  const {
    onFetch = null,
    onlyRunOnMount = false,
  } = args;

  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const { collection, setCollection } = useCollection(null);
  const mountedRef = useRef(null);

  const link = (typeof linkOrLinkUrl === 'string') ? fr.link({ data: { url: linkOrLinkUrl } }) : linkOrLinkUrl;

  async function fetchData(lnk) {
    try {
      setLoading(true);

      const resp = await lnk.get();
      if (mountedRef.current !== lnk.url()) {
        // The current link is stale, we shouldn't update any state with stale data
        return;
      }
      setCollection(resp);
      if (typeof onFetch === 'function') {
        await onFetch(resp);
      }
    } catch (err) {
      setLoadingError(err);
    }

    setLoading(false);

    if (!initialized) {
      setInitialized(true);
    }
  }

  useEffect(() => {
    if (onlyRunOnMount && mountedRef.current) {
      return;
    }

    // Keep track of the current link so we know if the results from the get() are stale
    mountedRef.current = link?.url();

    if (link) {
      fetchData(link);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      mountedRef.current = null;
    };
  }, [link?.url(), refreshCount]);

  const refreshCollection = () => {
    setRefreshCount(prevState => prevState + 1);
  };

  return {
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
  };
}
